/**
 * Image lightbox for inline figures (e.g. the Philosophy section diagram).
 *
 * Any element carrying `data-zoom` becomes an expand control: clicking it opens
 * its image in a full-screen overlay. A second click — on the enlarged image,
 * the dark backdrop, or the close button — drops the image back into place in
 * the page, as does pressing Escape or navigating to another section.
 *
 * Optional `data-zoom-src` overrides the enlarged source; `data-zoom-caption`
 * supplies the small caption shown under the enlarged image.
 */

/** @type {HTMLDivElement | null} */
let overlay = null;
/** @type {HTMLImageElement | null} */
let overlayImage = null;
/** @type {HTMLElement | null} */
let overlayCaption = null;
/** @type {HTMLButtonElement | null} */
let closeButton = null;
/** @type {HTMLElement | null} */
let activeTrigger = null;
/** @type {Element | null} */
let lastFocused = null;
/** @type {number | null} */
let hideTimer = null;
let bound = false;

/** Matches the CSS transition duration used for open/close. */
const TRANSITION_MS = 200;

function prefersReducedMotion() {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function getOverlay() {
  if (!overlay) {
    overlay = document.getElementById("image-lightbox");
    if (overlay) {
      overlayImage = overlay.querySelector(".lightbox-image");
      overlayCaption = overlay.querySelector(".lightbox-caption");
      closeButton = overlay.querySelector(".lightbox-close");
    }
  }
  return overlay;
}

function isOpen() {
  return Boolean(overlay && !overlay.hidden);
}

function openLightbox(trigger) {
  const box = getOverlay();
  if (!box || isOpen() || !overlayImage) return;

  const img = trigger.querySelector("img");
  if (!img) return;

  overlayImage.src = trigger.dataset.zoomSrc || img.currentSrc || img.src;
  overlayImage.alt = img.alt || "";
  if (overlayCaption) overlayCaption.textContent = trigger.dataset.zoomCaption || "";

  lastFocused = document.activeElement;
  activeTrigger = trigger;
  trigger.setAttribute("aria-expanded", "true");

  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }

  box.hidden = false;
  document.body.classList.add("lightbox-open");

  if (prefersReducedMotion()) {
    box.classList.add("is-open");
  } else {
    requestAnimationFrame(() => box.classList.add("is-open"));
  }

  if (closeButton) closeButton.focus();
}

function closeLightbox() {
  const box = getOverlay();
  if (!box || !isOpen()) return;

  box.classList.remove("is-open");
  document.body.classList.remove("lightbox-open");
  if (activeTrigger) activeTrigger.setAttribute("aria-expanded", "false");
  activeTrigger = null;

  const finish = () => {
    box.hidden = true;
    if (overlayImage) {
      overlayImage.removeAttribute("src");
      overlayImage.alt = "";
    }
    hideTimer = null;
  };

  if (prefersReducedMotion()) {
    finish();
  } else {
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(finish, TRANSITION_MS);
  }

  const restore = lastFocused;
  lastFocused = null;
  if (restore && typeof restore.focus === "function" && document.contains(restore)) {
    restore.focus({ preventScroll: true });
  }
}

/** Keep Tab inside the overlay while it is open (only the close button). */
function trapFocus(event) {
  if (event.key !== "Tab" || !isOpen() || !closeButton) return;
  event.preventDefault();
  closeButton.focus();
}

function handleClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const trigger = target.closest("[data-zoom]");
  if (trigger) {
    event.preventDefault();
    openLightbox(trigger);
    return;
  }

  // Any click inside the open overlay (image, caption, backdrop, close button)
  // collapses the image back into its place on the page.
  if (isOpen() && overlay.contains(target)) {
    closeLightbox();
  }
}

function handleKeydown(event) {
  if (event.key === "Escape") {
    closeLightbox();
    return;
  }
  trapFocus(event);
}

/**
 * Wire up every `[data-zoom]` figure plus the shared overlay.
 * Safe to call more than once; listeners are attached a single time.
 */
export function initImageLightbox() {
  if (!getOverlay()) return;
  if (bound) return;
  bound = true;

  document.addEventListener("click", handleClick);
  document.addEventListener("keydown", handleKeydown);
  window.addEventListener("hashchange", closeLightbox);
}